import { FC, useState } from 'react'

import styled from 'styled-components'

import { FullScreenModal } from 'src/Components/FullScreenModal'
import { ManualMatchingItem } from 'src/Components/ManualMatchingItem'
import { ManualMatchingModalFooter } from 'src/Components/ManualMatchingModalFooter'
import { useGetSpotifyPaymentSettingsQuery } from 'src/Redux/artistPaymentSettingsApi'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { numberToMoney } from 'src/Constants/functions'

interface Playlist {
  id: number
  playlistName: string
  followers: number
  playlistCover?: string
  playlistUrl?: string
}

interface Curator {
  id: number
  firstName: string
  lastName: string
  retailPrice: number
  photoUrl: string | null
  matchedPlaylists: Playlist[]
}

export interface ManualMatchingCampaignSummaryModalProps {
  curators: Curator[]
  onContinue: (selectedCuratorIds: number[]) => void
  isLoading?: boolean
  onClose?: () => void
}

const Container = styled.div`
  display: flex;
  padding: 24px 16px 80px 16px;
  flex-direction: column;
  gap: 24px;
  position: relative;

  @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
    margin: 0 auto;
    padding-left: 0px;
    padding-right: 0px;
    padding-top: 64px;
    width: 600px;
    padding-bottom: 24px;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Title = styled.h1`
  font-family: 'Segoe UI', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme }) => theme.color.darkGrey};
  margin: 0;
`

const Subtitle = styled.p`
  font-family: 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.darkGrey};
  margin: 0;
`

const CuratorsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 56px;
`

type Totals = {
  totalAmount: number
  totalPlaylists: number
  totalCurators: number
}

const MIN_SUGGESTED_CURATORS = 3
const MIN_REQUIRED_CURATORS = 1

export const ManualMatchingCampaignSummaryModal: FC<
  ManualMatchingCampaignSummaryModalProps
> = ({ curators, onContinue, isLoading, onClose }) => {
  const { data: spotifyPaymentSettings } = useGetSpotifyPaymentSettingsQuery(
    void 0,
    { refetchOnMountOrArgChange: true },
  )
  const manualMatchingMinBudget =
    spotifyPaymentSettings?.manualMatchingMinBudget

  const [selectedCurators, setSelectedCurators] = useState(
    curators.map((curator) => curator.id),
  )

  const handleCheckboxChange = (curatorId: number, checked: boolean): void => {
    if (checked) {
      setSelectedCurators((prev) =>
        prev.includes(curatorId) ? prev : [...prev, curatorId],
      )
    } else {
      setSelectedCurators((prev) => prev.filter((id) => id !== curatorId))
    }
  }

  const calculateTotals = (): Totals => {
    const selectedCuratorsData = curators.filter((curator) =>
      selectedCurators.includes(curator.id),
    )
    const amount = selectedCuratorsData.reduce(
      (sum, curator) => sum + curator.retailPrice,
      0,
    )
    const playlists = selectedCuratorsData.reduce(
      (sum, curator) => sum + curator.matchedPlaylists.length,
      0,
    )
    return {
      totalAmount: amount,
      totalPlaylists: playlists,
      totalCurators: selectedCuratorsData.length,
    }
  }

  const { totalAmount, totalPlaylists, totalCurators } = calculateTotals()

  const isMinBudgetKnown = typeof manualMatchingMinBudget === 'number'
  const isBelowMinBudget =
    isMinBudgetKnown && totalAmount < manualMatchingMinBudget
  const isContinueDisabled =
    selectedCurators.length < MIN_REQUIRED_CURATORS ||
    !isMinBudgetKnown ||
    isBelowMinBudget

  const handleContinue = (): void => {
    if (!isContinueDisabled) {
      onContinue(selectedCurators)
    }
  }

  return (
    <FullScreenModal
      showCloseButton
      fixedCloseButton={false}
      open
      closable
      prefixCls='manual-matching-campaign-summary'
      onCancel={onClose}
    >
      <Container>
        <Header>
          <Title>Campaign summary</Title>
          <Subtitle>
            For a stronger push, select at least {MIN_SUGGESTED_CURATORS}{' '}
            curators
          </Subtitle>
        </Header>

        <CuratorsSection>
          {curators.map((curator) => (
            <ManualMatchingItem
              key={curator.id}
              curatorName={`${curator.firstName} ${curator.lastName}`}
              playlistsCount={curator.matchedPlaylists.length}
              amountPerSubmission={`$${numberToMoney(curator.retailPrice)}`}
              avatarUrl={curator.photoUrl}
              playlists={curator.matchedPlaylists}
              checked={selectedCurators.includes(curator.id)}
              onCheckboxChange={(checked) =>
                handleCheckboxChange(curator.id, checked)
              }
            />
          ))}
        </CuratorsSection>

        <ManualMatchingModalFooter
          totalAmount={totalAmount}
          totalPlaylists={totalPlaylists}
          totalCurators={totalCurators}
          isBelowMinBudget={isBelowMinBudget}
          minBudget={manualMatchingMinBudget}
          disabled={isContinueDisabled}
          loading={isLoading}
          onContinue={handleContinue}
          onAddMoreCurators={onClose}
        />
      </Container>
    </FullScreenModal>
  )
}
